import type { fabric } from 'fabric';
import type { KindProfile, StyleField } from './types';

const prop = (key: string): StyleField => ({ key, storage: 'prop' });
const data = (key: string): StyleField => ({ key, storage: 'data' });
// A single marker field: its key doubles as the bucket key the captured
// `CapturedFillGradient` config is stored/read under.
const fillGradient = (): StyleField => ({ key: 'fillGradient', storage: 'fillGradient' });

// Shared by textbox / i-text / text — all render through the same inspector
// and carry the same Fabric props.
const TEXT_PROFILE: KindProfile = {
  fill: [prop('fill')],
  stroke: [prop('stroke'), prop('strokeWidth')],
  typography: [
    prop('fontFamily'),
    prop('fontWeight'),
    prop('fontStyle'),
    prop('fontSize'),
    prop('textAlign'),
    prop('lineHeight'),
    prop('underline'),
  ],
  opacity: [prop('opacity')],
  size: [prop('width')],
};

/**
 * kind → profile. The same semantic field maps to different storage per kind:
 * plain objects keep style in Fabric props, placeholders keep it in `obj.data`
 * (and require a refit after a data change — `applyStyle` handles that).
 *
 * placeholder-text deliberately has no `opacity`: `PlaceholderTextData` carries
 * no opacity field, so the value would not round-trip through a refit (the
 * `data` is the single source of truth for placeholder visuals).
 */
export const KIND_PROFILES: Record<string, KindProfile> = {
  rect: {
    // `fillGradient` mirrors/restores `data.fillGradient` through the façade —
    // the plain `fill` prop alone is meaningless once a gradient is active (it
    // holds a live `fabric.Gradient`, not a portable value). See capture-style.ts.
    fill: [prop('fill'), fillGradient()],
    stroke: [prop('stroke'), prop('strokeWidth')],
    opacity: [prop('opacity')],
    // `rx`/`ry` carry the uniform radius; `data.cornerRadii` carries the
    // per-corner override (rendered by the rect-corners plugin). Mirror the
    // placeholder-image profile so copy/paste style transfers per-corner radii.
    cornerRadius: [prop('rx'), prop('ry'), data('cornerRadii')],
    size: [prop('width'), prop('height')],
  },
  textbox: TEXT_PROFILE,
  'i-text': TEXT_PROFILE,
  text: TEXT_PROFILE,
  image: {
    opacity: [prop('opacity')],
    size: [prop('scaleX'), prop('scaleY')],
  },
  'placeholder-text': {
    fill: [data('fill')],
    typography: [
      data('fontFamily'),
      data('fontWeight'),
      data('fontStyle'),
      data('textAlign'),
      data('minFontSize'),
      data('maxFontSize'),
    ],
    size: [prop('width'), prop('height')],
  },
  'placeholder-image': {
    stroke: [data('stroke'), data('strokeWidth')],
    opacity: [data('opacity')],
    cornerRadius: [data('cornerRadius'), data('cornerRadii')],
    size: [prop('width'), prop('height')],
  },
};

/** Smart kinds dispatch on `data.kind`, built-in Fabric objects on `type`. */
export function resolveKind(object: fabric.Object): string {
  return (object.data as { kind?: string } | undefined)?.kind ?? object.type ?? 'unknown';
}

export function getKindProfile(object: fabric.Object): KindProfile | undefined {
  return KIND_PROFILES[resolveKind(object)];
}
